import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import config from "../../config";
import { useNavigate } from 'react-router-dom';
import "./Favourites.css";

const Favourites = () => {
  const {
    user,
    token,
    favourites,
    setFavourites,
    loading: authLoading,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const [favouritedKitchens, setFavouritedKitchens] = useState([]);
  const [imageIndexes, setImageIndexes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(true);
      return;
    }

    const loadFavouritedKitchens = async () => {
      setLoading(true);
      setError(null);
      if (favourites.length > 0) {
        try {
          const promises = favourites.map((kitchenID) =>
            axios
              .get(`${config.apiBaseUrl}/api/kitchens/kitchen/${kitchenID}`)
              .catch((e) => ({ error: e }))
          );
          const responses = await Promise.all(promises);

          const validKitchens = responses
            .filter((res) => !res.error)
            .map((res) => res.data);
          setFavouritedKitchens(validKitchens);

          const initialIndexes = validKitchens.reduce((acc, kitchen) => {
            acc[kitchen.KitchenID] = 0;
            return acc;
          }, {});
          setImageIndexes(initialIndexes);

          const errors = responses.filter((res) => res.error);
          if (errors.length > 0) {
            console.log("Some kitchens were not found:", errors);
          }
        } catch (error) {
          console.error("Error fetching favourited kitchens:", error);
          setError(
            "Failed to load favourited kitchens. Please try again later."
          );
        }
      } else {
        setFavouritedKitchens([]);
        setImageIndexes({});
      }
      setLoading(false);
    };
    loadFavouritedKitchens();
  }, [user, favourites, navigate]);

  const handleUnfavourite = async (kitchenID) => {
    if (!user) return;
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/auth/unfavourite`,
        { userID: user.userID, kitchenID: kitchenID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFavourites((prev) => prev.filter((id) => id !== kitchenID));
    } catch (error) {
      console.error("Error removing from favourites:", error);
    }
  };

  const handlePrev = (kitchenID) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImageIndexes((prev) => ({
      ...prev,
      [kitchenID]:
        prev[kitchenID] === 0
          ? favouritedKitchens.find((k) => k.KitchenID === kitchenID)?.Images
              ?.length - 1 || 0
          : prev[kitchenID] - 1,
    }));
  };

  const handleNext = (kitchenID) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    setImageIndexes((prev) => ({
      ...prev,
      [kitchenID]:
        prev[kitchenID] ===
        (favouritedKitchens.find((k) => k.KitchenID === kitchenID)?.Images
          ?.length - 1 || 0)
          ? 0
          : prev[kitchenID] + 1,
    }));
  };

  if (authLoading) {
    return <p className="favourites-message">Loading user data...</p>;
  }

  if (!user) {
    return (
      <p className="favourites-message">
        Please log in to view your favourited kitchens.
      </p>
    );
  }

  if (loading) {
    return (
      <p className="favourites-message">Loading your favourited kitchens...</p>
    );
  }

  if (error) {
    return <p className="favourites-message favourites-error">{error}</p>;
  }

  return (
    <div className="favourites-container">
      <h1>My Favourited Kitchens</h1>
      {favouritedKitchens.length > 0 ? (
        <div className="favourites-kitchen-list">
          {favouritedKitchens.map((kitchen) => {
            const defaultImage =
              "https://airkitchenz-assets.s3.us-west-1.amazonaws.com/assets/KitchenComingSoon.png";
            const imageArray =
              kitchen.Images && kitchen.Images.length > 0
                ? kitchen.Images
                : [defaultImage];
            const currentIndex = imageIndexes[kitchen.KitchenID] || 0;

            return (
              <div key={kitchen.KitchenID} className="favourites-kitchen-item">
                <div className="favourites-image-container">
                  <div
                    className="favourites-image-slider"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                  >
                    {imageArray.map((src, index) => (
                      <img
                        key={index}
                        src={src}
                        alt={`${kitchen.Title} - Image ${index + 1}`}
                        className="favourites-kitchen-image"
                        loading="lazy"
                      />
                    ))}
                  </div>
                  {imageArray.length > 1 && (
                    <>
                      <button
                        className="favourites-img-arrow favourites-arrow-left"
                        onClick={handlePrev(kitchen.KitchenID)}
                      >
                        ❮
                      </button>
                      <button
                        className="favourites-img-arrow favourites-arrow-right"
                        onClick={handleNext(kitchen.KitchenID)}
                      >
                        ❯
                      </button>
                    </>
                  )}
                </div>
                <div className="favourites-kitchen-details">
                  <div className="favourites-kitchen-info">
                    <h3>{kitchen.Title}</h3>
                    <p>
                      <strong>Location:</strong> {kitchen.City}
                    </p>
                    <p>
                      <strong>Area:</strong> {kitchen.Size}
                    </p>
                    <p>
                      <strong>Type:</strong> {kitchen.Category}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUnfavourite(kitchen.KitchenID)}
                    className="favourites-unfavourite-btn"
                  >
                    Unfavourite
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="favourites-message">
          You have no favourited kitchens yet.
        </p>
      )}
    </div>
  );
};

export default Favourites;